/**
 * File Validation Utilities for chat / admin attachments
 *
 * Validates file types and sizes according to backend requirements:
 * - Images: jpg, png, gif, webp, bmp (max 20MB)
 * - Videos: mp4, mov, webm, avi (max 100MB)
 * - Documents (opt-in via `allowDocuments`): pdf, ppt/pptx, doc/docx,
 *   xls/xlsx, txt, csv, md (max 50MB) — FAQ attachments only today.
 *
 * Default behaviour (no options) is unchanged: images + videos only.
 */

// Allowed file types
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp'
];

export const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',  // .mov
  'video/webm',
  'video/x-msvideo'   // .avi
];

export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'text/markdown'
];

// Browsers sometimes report Office files as application/octet-stream — the
// extension decides in that case (mirrors the backend's classify()).
export const DOCUMENT_EXTENSIONS = ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'md'];

// Maximum file sizes in bytes
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024;     // 20 MB
export const MAX_VIDEO_SIZE = 100 * 1024 * 1024;    // 100 MB
export const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024;  // 50 MB

const extensionOf = (name) => {
  const m = String(name || '').match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : '';
};

/**
 * Classify a file as 'image' | 'video' | 'document' | null (not allowed).
 * @param {File} file
 * @param {boolean} allowDocuments
 */
export const classifyFile = (file, allowDocuments = false) => {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) return 'image';
  if (ALLOWED_VIDEO_TYPES.includes(file.type)) return 'video';
  if (allowDocuments && (
    ALLOWED_DOCUMENT_TYPES.includes(file.type) || DOCUMENT_EXTENSIONS.includes(extensionOf(file.name))
  )) {
    return 'document';
  }
  return null;
};

/**
 * Check if file type is allowed
 * @param {File} file - File object to validate
 * @param {boolean} allowDocuments - Also accept PDF/Office/text documents
 * @returns {boolean} - True if file type is valid
 */
export const isValidFileType = (file, allowDocuments = false) => {
  return classifyFile(file, allowDocuments) !== null;
};

const maxSizeFor = (fileType) => {
  if (fileType === 'video') return MAX_VIDEO_SIZE;
  if (fileType === 'document') return MAX_DOCUMENT_SIZE;
  return MAX_IMAGE_SIZE;
};

/**
 * Check if file size is within limits
 * @param {File} file - File object to validate
 * @param {boolean} allowDocuments
 * @returns {boolean} - True if file size is valid
 */
export const isValidFileSize = (file, allowDocuments = false) => {
  const fileType = classifyFile(file, allowDocuments) || 'image';
  return file.size <= maxSizeFor(fileType);
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "5.2 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Don't show decimal for bytes
  if (i === 0) {
    return bytes + ' ' + sizes[i];
  }

  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
};

/**
 * Comprehensive file validation
 * @param {File} file - File object to validate
 * @param {Object} [options]
 * @param {boolean} [options.allowDocuments=false] - Accept PDF/Office/text too
 * @returns {Object} - { valid: boolean, error?: string, fileType?: 'image'|'video'|'document' }
 */
export const validateFile = (file, options = {}) => {
  const { allowDocuments = false } = options;
  const fileType = classifyFile(file, allowDocuments);

  // Validate file type
  if (!fileType) {
    return {
      valid: false,
      error: allowDocuments
        ? 'Invalid file type. Images, videos, PDF, PowerPoint, Word, Excel and text files are allowed.'
        : 'Invalid file type. Only images and videos are allowed.'
    };
  }

  // Validate file size
  const maxSize = maxSizeFor(fileType);
  if (file.size > maxSize) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    const maxSizeMB = (maxSize / 1024 / 1024).toFixed(0);

    return {
      valid: false,
      error: `File size (${sizeMB}MB) exceeds maximum allowed size (${maxSizeMB}MB)`
    };
  }

  return {
    valid: true,
    fileType
  };
};

/**
 * Get file icon based on file type
 * @param {string} fileType - 'image' | 'video' | 'document'
 * @returns {string} - Emoji icon
 */
export const getFileIcon = (fileType) => {
  if (fileType === 'video') return '🎥';
  if (fileType === 'document') return '📎';
  return '📷';
};
